import type { ComponentProps, PropsWithChildren, ReactElement } from 'react';
import { memo, useCallback } from 'react';

import { useIsLargeScreen } from '@bestseller-bit/frontend-community.theme.buying-theme';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { OrderStatesTypes } from '../../../types/OrderStates';
import { TruncatedText } from '../../TruncatedText';

interface SharedCellTypographyProps
    extends Omit<ComponentProps<typeof Typography>, 'variant'> {
    /* Optional order state type name to determine text color based on the order state */
    orderStateTypeName?: string;
    /* Size of the text, large (default) or small */
    size?: 'lg' | 'sm';
    /* If true, the text truncates with an ellipsis when it overflows, and reveals its full value in a tooltip while truncated */
    ellipsis?: boolean;
    /* If true, the text will use balanced wrapping, will be ignored if ellipsis is true */
    balanceText?: boolean;
}

type GetVariantType =
    | 'bodySmall.default'
    | 'captions.default'
    | 'captionsSmall.default';

const SharedCellTypography = memo(
    ({
        orderStateTypeName,
        size = 'lg',
        ellipsis = false,
        balanceText = false,
        children,
        ...typographyProps
    }: PropsWithChildren<SharedCellTypographyProps>): ReactElement => {
        const { palette } = useTheme();
        const isLargeScreen = useIsLargeScreen();

        const textColor =
            orderStateTypeName === OrderStatesTypes.cancelled
                ? alpha(palette.primary.main, 0.2)
                : palette.primary.main;

        const getVariant = useCallback((): GetVariantType => {
            if (size === 'lg') {
                return isLargeScreen ? 'bodySmall.default' : 'captions.default';
            } else {
                return isLargeScreen
                    ? 'captions.default'
                    : 'captionsSmall.default';
            }
        }, [isLargeScreen, size]);

        const sharedProps = {
            variant: getVariant(),
            ...typographyProps,
            sx: {
                color: textColor,
                ...(balanceText &&
                    !ellipsis && {
                        textWrap: 'balance',
                    }),
                ...(typographyProps.sx as object),
            },
        };

        // TruncatedText brings the ellipsis itself, along with a tooltip while truncated.
        if (ellipsis) {
            return <TruncatedText {...sharedProps}>{children}</TruncatedText>;
        }

        return <Typography {...sharedProps}>{children}</Typography>;
    }
);

SharedCellTypography.displayName = 'SharedCellTypography';

export { SharedCellTypography };
